import { business, fullAddress } from "./business";

export const SITE_NAME = "Chavel Chaveiro Betim";
export const SITE_URL = "https://chavelbetim.com.br";

export function absoluteUrl(path: string) {
  if (path.startsWith("http")) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function canonicalLink(path: string) {
  return [{ rel: "canonical", href: absoluteUrl(path) }];
}

export function localBusinessSchema(extra?: Record<string, unknown>) {
  return {
    "@context": "https://schema.org",
    "@type": "Locksmith",
    "@id": "https://chavelbetim.com.br/#chavel",
    name: business.name,
    alternateName: business.brand,
    url: SITE_URL,
    description:
      "Chaveiro em Betim desde 1975. Abertura de portas e veículos, chaves codificadas, fechaduras, fechadura digital e controles de portão.",
    telephone: business.phone,
    foundingDate: String(business.since),
    address: {
      "@type": "PostalAddress",
      streetAddress: business.street,
      addressLocality: business.city,
      addressRegion: business.region,
      postalCode: business.postalCode,
      addressCountry: "BR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: business.latitude,
      longitude: business.longitude,
    },
    areaServed: business.neighborhoods.map((n) => ({
      "@type": "Place",
      name: `${n}, ${business.city} - ${business.region}`,
    })),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: business.rating,
      reviewCount: business.reviewCount,
    },
    openingHoursSpecification: business.schemaHours.map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: h.days,
      opens: h.opens,
      closes: h.closes,
    })),
    priceRange: "$$",
    ...extra,
  };
}

export function faqSchema(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

export function serviceSchema(name: string, description: string, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url,
    serviceType: name,
    provider: { "@type": "Locksmith", name: business.name, telephone: business.phone, address: fullAddress },
    areaServed: { "@type": "City", name: `${business.city} - ${business.regionName}` },
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.url),
    })),
  };
}

export function metaFor(opts: {
  title: string;
  description: string;
  path: string;
  type?: string;
  image?: string;
}) {
  const meta: Array<Record<string, string>> = [
    { title: opts.title },
    { name: "description", content: opts.description },
    { property: "og:title", content: opts.title },
    { property: "og:description", content: opts.description },
    { property: "og:type", content: opts.type ?? "website" },
    { property: "og:url", content: absoluteUrl(opts.path) },
    { property: "og:locale", content: "pt_BR" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: opts.title },
    { name: "twitter:description", content: opts.description },
    { name: "geo.region", content: "BR-MG" },
    { name: "geo.placename", content: `${business.city}, ${business.regionName}` },
    { name: "geo.position", content: `${business.latitude};${business.longitude}` },
    { name: "ICBM", content: `${business.latitude}, ${business.longitude}` },
  ];
  if (opts.image) {
    meta.push({ property: "og:image", content: absoluteUrl(opts.image) });
    meta.push({ name: "twitter:image", content: absoluteUrl(opts.image) });
  }
  return meta;
}
